import { useEffect } from 'react';

/**
 * Hook to handle mobile keyboard visibility and adjust viewport height accordingly.
 *
 * This hook uses the Visual Viewport API to detect when the virtual keyboard opens/closes
 * on mobile devices and adjusts the app container height to ensure content remains visible.
 *
 * The Visual Viewport API is the most reliable cross-browser solution for detecting
 * keyboard visibility on both Android and iOS devices.
 */
export const useKeyboardResize = () => {
    useEffect(() => {
        // Only run in browser environment
        if (typeof window === 'undefined' || !window.visualViewport) {
            return;
        }

        const viewport = window.visualViewport;
        const appElement = document.querySelector('.EnhancedChatGptApp') as HTMLElement;

        if (!appElement) {
            return;
        }

        /**
         * Handler for viewport resize events.
         * Updates the app container height to match the visible viewport height.
         */
        const handleViewportResize = () => {
            if (viewport && appElement) {
                // Set height to visual viewport height (excludes keyboard)
                appElement.style.height = `${viewport.height}px`;
            }
        };

        /**
         * Handler for viewport scroll events.
         * Ensures the height stays synchronized during keyboard animations.
         */
        const handleViewportScroll = () => {
            if (viewport && appElement) {
                appElement.style.height = `${viewport.height}px`;
            }
        };

        // Initial height setup
        handleViewportResize();

        // Listen for viewport changes
        viewport.addEventListener('resize', handleViewportResize);
        viewport.addEventListener('scroll', handleViewportScroll);

        // Cleanup
        return () => {
            viewport.removeEventListener('resize', handleViewportResize);
            viewport.removeEventListener('scroll', handleViewportScroll);

            // Reset height on cleanup
            if (appElement) {
                appElement.style.height = '';
            }
        };
    }, []);
};
